/**
 * Subscription Plans Page
 * Displays pricing tiers and allows service providers to subscribe or upgrade
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionService } from '../services/subscription.service';
import { serviceProviderService, type Subscription, type SubscriptionTier } from '../services/service-provider.service';
import { toast } from '@/lib/utils/toast';

interface PlanTier {
  name: string;
  tier: SubscriptionTier;
  price: number;
  priceLabel: string;
  description: string;
  features: string[];
  jobLimit: string;
  transactionFee: string;
  marketplaceAccess: boolean;
  highlighted?: boolean;
}

const PLAN_TIERS: PlanTier[] = [
  {
    name: 'Free',
    tier: 'free',
    price: 0,
    priceLabel: '$0',
    description: 'Perfect for getting started',
    jobLimit: '3 jobs per month',
    transactionFee: '5% transaction fee',
    marketplaceAccess: false,
    features: [
      '3 jobs per month',
      'Single county access',
      '5% transaction fee',
      'Basic support',
      'Email notifications',
    ],
  },
  {
    name: 'Basic',
    tier: 'basic',
    price: 99,
    priceLabel: '$99',
    description: 'Great for growing businesses',
    jobLimit: 'Unlimited jobs',
    transactionFee: '5% transaction fee',
    marketplaceAccess: true,
    features: [
      'Unlimited jobs',
      'Multi-county access',
      'Marketplace access',
      '5% transaction fee',
      'Priority email support',
      'Advanced analytics',
    ],
  },
  {
    name: 'Pro',
    tier: 'pro',
    price: 249,
    priceLabel: '$249',
    description: 'For established providers',
    jobLimit: 'Unlimited jobs',
    transactionFee: '4% transaction fee',
    marketplaceAccess: true,
    highlighted: true,
    features: [
      'Unlimited jobs',
      'Multi-county access',
      'Marketplace access',
      '4% transaction fee',
      'Priority support',
      'Advanced analytics',
      'Custom reporting',
      'API access',
    ],
  },
  {
    name: 'Enterprise',
    tier: 'enterprise',
    price: 499,
    priceLabel: '$499',
    description: 'For large-scale operations',
    jobLimit: 'Unlimited jobs',
    transactionFee: '3% transaction fee',
    marketplaceAccess: true,
    features: [
      'Unlimited jobs',
      'Multi-county access',
      'Marketplace access',
      '3% transaction fee',
      'Dedicated account manager',
      'Phone & email support',
      'Advanced analytics',
      'Custom reporting',
      'API access',
      'White-label options',
    ],
  },
];

export const SubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingTier, setProcessingTier] = useState<SubscriptionTier | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check authentication
    if (!serviceProviderService.isAuthenticated()) {
      navigate('/provider/login');
      return;
    }

    loadSubscription();
  }, [navigate]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      const subscription = await subscriptionService.getSubscription();
      setCurrentSubscription(subscription);
    } catch (err) {
      console.error('Failed to load subscription:', err);
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier: SubscriptionTier) => {
    if (tier === 'free') {
      // Cannot subscribe to free tier via checkout
      return;
    }

    try {
      setProcessingTier(tier);
      setError(null);

      const response = await subscriptionService.createCheckoutSession(tier);

      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError('Failed to start checkout process. Please try again.');
      setProcessingTier(null);
    }
  };

  const handleDowngrade = (tier: SubscriptionTier) => {
    if (tier === 'free') {
      const confirmed = window.confirm(
        'Are you sure you want to downgrade to the free plan? This will limit you to 3 jobs per month and remove marketplace access.'
      );
      if (confirmed) {
        toast.info('Downgrade functionality coming soon. Please contact support.');
      }
    }
  };

  const getButtonConfig = (planTier: SubscriptionTier) => {
    if (loading || !currentSubscription) {
      return { label: 'Loading...', disabled: true, onClick: () => {} };
    }

    const currentTier = currentSubscription.tier;

    if (currentTier === planTier) {
      return { label: 'Current Plan', disabled: true, onClick: () => {} };
    }

    const tierOrder = { free: 0, basic: 1, pro: 2, enterprise: 3 };
    const isUpgrade = tierOrder[planTier] > tierOrder[currentTier];

    if (planTier === 'free') {
      return {
        label: 'Downgrade',
        disabled: false,
        onClick: () => handleDowngrade(planTier),
        variant: 'secondary' as const,
      };
    }

    return {
      label: isUpgrade ? 'Upgrade' : 'Subscribe',
      disabled: processingTier !== null,
      onClick: () => handleSubscribe(planTier),
      variant: 'primary' as const,
    };
  };

  if (!serviceProviderService.isAuthenticated()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-neutral-900 mb-4">
            Choose Your Subscription Plan
          </h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Select the plan that best fits your business needs. Upgrade or downgrade at any time.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="card bg-red-50 border-red-200 mb-6 max-w-2xl mx-auto">
            <p className="text-red-900">{error}</p>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {PLAN_TIERS.map((plan) => {
            const buttonConfig = getButtonConfig(plan.tier);
            const isCurrentPlan = currentSubscription?.tier === plan.tier;

            return (
              <div
                key={plan.tier}
                className={`card flex flex-col ${
                  plan.highlighted
                    ? 'ring-2 ring-primary-600 shadow-lg'
                    : isCurrentPlan
                    ? 'ring-2 ring-green-500'
                    : ''
                }`}
              >
                {/* Plan Header */}
                <div className="mb-6">
                  {plan.highlighted && (
                    <span className="inline-block px-3 py-1 text-xs font-semibold text-white bg-primary-600 rounded-full mb-4">
                      Most Popular
                    </span>
                  )}
                  {isCurrentPlan && (
                    <span className="inline-block px-3 py-1 text-xs font-semibold text-white bg-green-600 rounded-full mb-4">
                      Your Plan
                    </span>
                  )}
                  <h3 className="text-2xl font-bold text-neutral-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-neutral-600 text-sm mb-4">{plan.description}</p>
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl font-bold text-neutral-900">
                      {plan.priceLabel}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-neutral-600 ml-2">/month</span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-600">{plan.jobLimit}</p>
                  <p className="text-sm text-neutral-600">{plan.transactionFee}</p>
                </div>

                {/* Features List */}
                <div className="flex-grow mb-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg
                          className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-neutral-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <button
                  onClick={buttonConfig.onClick}
                  disabled={buttonConfig.disabled}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                    buttonConfig.disabled
                      ? 'bg-neutral-300 text-neutral-600 cursor-not-allowed'
                      : buttonConfig.variant === 'primary'
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-white text-neutral-900 border-2 border-neutral-300 hover:border-neutral-400'
                  }`}
                >
                  {processingTier === plan.tier ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    buttonConfig.label
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="card max-w-3xl mx-auto bg-blue-50 border-blue-200">
          <div className="flex items-start">
            <svg
              className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">
                All plans include:
              </h4>
              <ul className="space-y-1 text-blue-800 text-sm">
                <li>• Secure payment processing via Stripe</li>
                <li>• Real-time job notifications</li>
                <li>• Mobile-responsive dashboard</li>
                <li>• Monthly billing statements</li>
                <li>• Cancel anytime</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
